import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { ApiRequestService } from '../shared/api-request.service';
import { UtilsService } from '../shared/utils.service';

/**
 * Public page that verifies an email address from a token in the link.
 *
 * On load it submits the token to the public verification endpoint and shows one of: a thank-you on
 * success, an expired or already-used message with a resend option, or a generic invalid-link message.
 * No authentication is required.
 */
@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrls: ['./verify-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VerifyEmailComponent implements OnInit {
  // loading | success | expired | used | unknown
  status: string = 'loading';

  token: string = '';

  resendEmail: string = '';
  resendSent: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private api: ApiRequestService,
    public utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.params['token'];
    this.verify();
  }

  private verify(): void {
    this.status = 'loading';

    this.api
      .makeRequest('post', 'v2/verify-email', { token: this.token })
      .then(() => {
        this.status = 'success';
      })
      .catch((errorResponse) => {
        const reason =
          errorResponse && errorResponse.error
            ? errorResponse.error.reason
            : null;
        const httpStatus = errorResponse ? errorResponse.status : null;

        if (httpStatus === 410 || reason === 'expired') {
          this.status = 'expired';
        } else if (httpStatus === 409 || reason === 'used') {
          this.status = 'used';
        } else {
          this.status = 'unknown';
        }
      });
  }

  /**
   * Request a fresh verification email. The endpoint always responds generically so it cannot be used
   * to discover which addresses exist.
   */
  onResend(form: NgForm): void {
    if (!form.valid || !this.resendEmail) {
      this.utils.showToast('Please enter your email address.');
      return;
    }

    this.api
      .makeRequest('post', 'v2/verify-email/resend', {
        email: this.resendEmail
      })
      .then((response) => {
        this.resendSent = true;
        this.utils.showToast(
          response && response.message
            ? response.message
            : 'If that address needs verification, a new link has been sent.'
        );
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }
}
